/**
 * The menu, grouped by job (SIMPLE-UI-AND-FOOD-TRACE-DESIGN-2026-11-05.md section 3). These groups say only where each
 * `nav-*` screen sits. Who sees what stays where it was: the server's experience and `navItemVisible` in App.js choose
 * the items, and this file only places them. An id with no item behind it draws nothing, so `nav-use-stock`,
 * `nav-prepare-food` and `nav-food-trace` can be listed before their screens exist.
 */
export const NAV_GROUPS = Object.freeze([
  { id: 'core', title: null, items: ['nav-today', 'nav-invoices'] },
  { id: 'stock', title: 'Stock',
    items: ['nav-use-stock', 'nav-cut-cost-inventory', 'nav-cut-cost-count', 'nav-cut-cost-waste', 'nav-receiving-putaway'] },
  { id: 'kitchen', title: 'Kitchen',
    items: ['nav-prepare-food', 'nav-cut-cost-ck-ops', 'nav-cut-cost-recipes', 'nav-cut-cost-chef-confirm',
      'nav-cut-cost-ppds-label', 'nav-cut-cost-menu-items', 'nav-run-service-menu-config', 'nav-make-money-kds',
      'nav-cut-cost-forecast', 'nav-cut-cost-demand-forecast'] },
  { id: 'deliveries', title: 'Deliveries',
    items: ['nav-receiving-queue', 'nav-receiving-capture', 'nav-receiving-inbox', 'nav-receiving-reviews',
      'nav-receiving-claims', 'nav-receiving-sync', 'nav-make-money-delivery'] },
  { id: 'money', title: 'Money',
    items: ['nav-cut-cost-review', 'nav-cut-cost-vendors', 'nav-cut-cost-ck-billing', 'nav-cut-cost-ck-price-lists',
      'nav-cut-cost-supplier-price-lists', 'nav-cut-cost-procurement', 'nav-cut-cost-marketplace',
      'nav-cut-cost-marketplace-ops', 'nav-cut-cost-roi', 'nav-cut-cost-price-impact', 'nav-cut-cost-site-rollup',
      'nav-group-view', 'nav-cut-cost-calculator', 'nav-cut-cost-decisions', 'nav-make-money-close', 'nav-settings-xero'] },
  // Two ids open /scan. Both are listed so that either one still draws, and groupNav keeps the first it meets.
  { id: 'food-safety', title: 'Food safety',
    items: ['nav-food-trace', 'nav-cut-cost-scan-station', 'nav-traceability-station', 'nav-traceability-gates',
      'nav-receiving-settings', 'nav-stay-compliant-alerts', 'nav-stay-compliant-safety',
      'nav-stay-compliant-controls', 'nav-stay-compliant-visual-safety', 'nav-stay-compliant-documents'] },
  { id: 'settings', title: 'Settings',
    items: ['nav-admin-console', 'nav-settings-dashboard', 'nav-settings-team', 'nav-settings-access',
      'nav-settings-security', 'nav-settings-setup', 'nav-settings-compliance', 'nav-settings-imports',
      'nav-settings-destinations', 'nav-settings-integrations', 'nav-settings-ai', 'nav-settings-dataroom',
      'nav-settings-billing', 'nav-settings-persona', 'nav-settings-superadmin', 'nav-settings-implementations',
      'nav-settings-proposals', 'nav-run-service-onboarding', 'nav-run-service-first-run', 'nav-run-service-n8n'] },
]);

export const MORE_GROUP = Object.freeze({ id: 'more', title: 'More' });

const PLACED = new Set(NAV_GROUPS.flatMap((group) => group.items));

/**
 * The items a person may see, in their groups. An item no group names goes in "More", so no screen can be lost. Two
 * items that open the same path show once. Groups with nothing to show are left out.
 */
export function groupNav(items) {
  const byId = new Map(items.map((item) => [item.testid, item]));
  const listed = new Set();
  const once = (list) => list.filter((item) => {
    if (!item || listed.has(item.to)) return false;
    listed.add(item.to);
    return true;
  });
  const groups = NAV_GROUPS.map((group) => ({ ...group, items: once(group.items.map((id) => byId.get(id))) }));
  const more = { ...MORE_GROUP, items: once(items.filter((item) => !PLACED.has(item.testid))) };
  return [...groups, more].filter((group) => group.items.length > 0);
}

/**
 * The item for this address. An item with a query string matches only the exact address; any other matches its path
 * and the paths under it, and the longest of those wins (/ck-ops/billing is Delivery billing, not CK operations).
 */
export function activeItem(items, { pathname, search = '' }) {
  const exact = items.find((item) => item.to.includes('?') && item.to === `${pathname}${search}`);
  if (exact) return exact;
  let best = null;
  for (const item of items) {
    if (item.to.includes('?')) continue;
    if ((pathname === item.to || pathname.startsWith(`${item.to}/`)) && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}

/** Below this width the top bar and its drawer stay. */
export const PANEL_QUERY = '(min-width: 1024px)';
